import type { DownloadProductCategory } from "./download-types";

const CATEGORY_BASE_PADDING = 20;
const CATEGORY_INDENT = 20;
const MAX_CATEGORY_PADDING = 40;
const PRODUCT_BASE_PADDING = 30;
const PRODUCT_INDENT = 14;
const MAX_PRODUCT_PADDING = 44;

/** 收集同级分类及其后代分类 ID，供手风琴分支互斥使用。 */
export function getCategoryTreeIds(
  categories: readonly DownloadProductCategory[],
): number[] {
  return categories.flatMap((category) => [
    category.id,
    ...getCategoryTreeIds(category.children),
  ]);
}

/** 切换手风琴分类，并关闭同级分类及其后代分支。 */
export function toggleAccordionCategory(
  values: ReadonlySet<number>,
  categoryId: number,
  categories: readonly DownloadProductCategory[],
) {
  const next = new Set(values);
  const shouldExpand = !next.has(categoryId);
  getCategoryTreeIds(categories).forEach((id) => next.delete(id));
  if (shouldExpand) next.add(categoryId);
  return next;
}

/** 将当前产品链接滚动到左侧目录可视范围内。 */
export function scrollActiveProductIntoView(
  container: HTMLDivElement | null,
) {
  const activeLink = container?.querySelector<HTMLElement>(
    '[aria-current="page"]',
  );
  if (!container || !activeLink) return;
  const containerRect = container.getBoundingClientRect();
  const activeRect = activeLink.getBoundingClientRect();
  if (activeRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - activeRect.top;
  } else if (activeRect.bottom > containerRect.bottom) {
    container.scrollTop += activeRect.bottom - containerRect.bottom;
  }
}

/** 计算分类行缩进，并将深层分类的视觉缩进封顶。 */
export function getCategoryPadding(depth: number) {
  return Math.min(
    CATEGORY_BASE_PADDING + depth * CATEGORY_INDENT,
    MAX_CATEGORY_PADDING,
  );
}

/** 计算产品行内边距，并保留深层分类的数据层次。 */
export function getProductPadding(depth: number) {
  return Math.min(
    PRODUCT_BASE_PADDING + Math.max(0, depth - 1) * PRODUCT_INDENT,
    MAX_PRODUCT_PADDING,
  );
}

/** 获取当前产品祖先分类；未找到时展开首个分类。 */
export function getInitialExpandedCategoryIds(
  categories: DownloadProductCategory[],
  currentProductId: number | null,
) {
  return (
    (currentProductId === null
      ? undefined
      : findProductCategoryPath(categories, currentProductId)) ??
    (categories[0] ? [categories[0].id] : [])
  );
}

/** 判断分类或任意后代分类中是否包含指定产品。 */
export function categoryContainsProduct(
  category: DownloadProductCategory,
  productId: number | null,
): boolean {
  if (productId === null) return false;
  if (category.products.some((product) => product.id === productId)) {
    return true;
  }
  return category.children.some((child) =>
    categoryContainsProduct(child, productId),
  );
}

/** 根据搜索词递归过滤产品分类树。 */
export function filterProductCategories(
  categories: DownloadProductCategory[],
  query: string,
): DownloadProductCategory[] {
  const keyword = normalizeSearchValue(query);
  if (!keyword) return categories;
  return categories.flatMap((category) => {
    const filtered = filterProductCategory(category, keyword);
    return filtered ? [filtered] : [];
  });
}

/** 规范化搜索文本。 */
export function normalizeSearchValue(value: string) {
  return value.trim().toLocaleLowerCase();
}

/** 查找当前产品所属分类及其祖先路径。 */
function findProductCategoryPath(
  categories: DownloadProductCategory[],
  currentProductId: number,
): number[] | undefined {
  for (const category of categories) {
    if (category.products.some((product) => product.id === currentProductId)) {
      return [category.id];
    }
    const childPath = findProductCategoryPath(
      category.children,
      currentProductId,
    );
    if (childPath) return [category.id, ...childPath];
  }
  return undefined;
}

/** 过滤单个分类节点。 */
function filterProductCategory(
  category: DownloadProductCategory,
  keyword: string,
): DownloadProductCategory | null {
  if (normalizeSearchValue(category.name).includes(keyword)) return category;
  const children = category.children.flatMap((child) => {
    const filtered = filterProductCategory(child, keyword);
    return filtered ? [filtered] : [];
  });
  const products = category.products.filter((product) =>
    productMatchesKeyword(product, keyword),
  );
  if (children.length === 0 && products.length === 0) return null;
  return { ...category, children, products };
}

/** 判断产品名称、别名或编码是否匹配搜索词。 */
function productMatchesKeyword(
  product: DownloadProductCategory["products"][number],
  keyword: string,
) {
  return [product.name, product.nickname, product.code].some((value) => {
    return normalizeSearchValue(value).includes(keyword);
  });
}
